import { useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { type LiveSessionSummary } from '../api/live';
import { useFormatTime } from '../util/displayTimeZone';
import { type TraitFrame } from '../replay/buildTimeline';
import { useLiveReplay, type LiveScope } from '../replay/useLiveReplay';
import { TRAIT_KEYS, getRule } from '../replay/traitScoring';
import { StandbyGoBanner } from './StandbyGoBanner';

/**
 * One live session as an embeddable tile (LIVE-WALL). Runs its own follow
 * -live engine via useLiveReplay and renders adaptively: `solo` shows the
 * full widget set (focus + 6 traits + EEG bands + hand motion); `tile`
 * shows a compact card (focus + a live motion sparkline) for the 2–4-up
 * grid, with an expand affordance to open the full replay dialog.
 */

const BAND_WINDOW_S = 30;
const ACCEL_WINDOW_S = 4;
const BAND_SERIES = [
  { key: 'delta', color: '#5c6bc0' },
  { key: 'theta', color: '#26a69a' },
  { key: 'alpha', color: '#66bb6a' },
  { key: 'beta', color: '#ffa726' },
  { key: 'gamma', color: '#ef5350' },
] as const;

function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Last frame with `.t <= t` (binary search over a time-sorted array). */
function frameAt<T extends { t: number }>(arr: T[], t: number): T | null {
  let lo = 0;
  let hi = arr.length - 1;
  let res: T | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t <= t) {
      res = arr[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return res;
}

function LiveDot() {
  return (
    <Box
      component="span"
      sx={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        bgcolor: 'error.main',
        flexShrink: 0,
        animation: 'lwpulse 1.4s ease-in-out infinite',
        '@keyframes lwpulse': {
          '0%,100%': { opacity: 1 },
          '50%': { opacity: 0.25 },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    />
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'capitalize' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function BandLegend() {
  return (
    <Stack direction="row" spacing={1.25} sx={{ pl: 0.5, flexWrap: 'wrap' }}>
      {BAND_SERIES.map((b) => (
        <LegendDot key={b.key} color={b.color} label={b.key} />
      ))}
    </Stack>
  );
}

function TraitBar({
  label,
  color,
  pct,
  emphasize = false,
}: {
  label: string;
  color: string;
  pct: number | null;
  emphasize?: boolean;
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography
          variant="caption"
          sx={{ color, fontWeight: 700, fontSize: emphasize ? '0.8rem' : undefined }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 800, fontSize: emphasize ? '0.8rem' : undefined }}
        >
          {pct === null ? '—' : `${pct}%`}
        </Typography>
      </Stack>
      <Box
        sx={{
          height: emphasize ? 8 : 6,
          borderRadius: 4,
          bgcolor: 'action.hover',
          mt: 0.25,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.max(0, Math.min(100, pct ?? 0))}%`,
            bgcolor: color,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
    </Box>
  );
}

export interface LiveSessionTileProps {
  scope: LiveScope | { mine: true } | { tenantId: string };
  session: LiveSessionSummary;
  variant: 'solo' | 'tile';
  /** Fired once when the session's stream signals it ended. */
  onEnded?: (sessionId: string) => void;
  /** Open the full-screen replay dialog for this session. */
  onExpand?: (session: LiveSessionSummary) => void;
}

export function LiveSessionTile({
  scope,
  session,
  variant,
  onEnded,
  onExpand,
}: LiveSessionTileProps) {
  const live: LiveScope =
    'mine' in scope
      ? { mine: true, sessionId: session.session_id }
      : { tenantId: (scope as { tenantId: string }).tenantId, sessionId: session.session_id };

  const { timeline, tSec, endedLive, connected, streaming } = useLiveReplay(
    live,
    true,
  );

  // Bubble the end up exactly once so the wall can drop the tile and
  // promote the next queued session.
  useEffect(() => {
    if (endedLive) onEnded?.(session.session_id);
  }, [endedLive, onEnded, session.session_id]);

  const traitFrame: TraitFrame | null = timeline
    ? frameAt(timeline.traits, tSec)
    : null;

  const fmtTime = useFormatTime();
  const accelWindow = useMemo(() => {
    if (!timeline) return [];
    const lo = tSec - ACCEL_WINDOW_S;
    return timeline.accel.filter((f) => f.t >= lo && f.t <= tSec + 0.001);
  }, [timeline, tSec]);

  const bandWindow = useMemo(() => {
    if (!timeline) return [];
    const lo = tSec - BAND_WINDOW_S;
    return timeline.bands.filter((f) => f.t >= lo && f.t <= tSec + 0.001);
  }, [timeline, tSec]);

  const solo = variant === 'solo';
  const participant = session.participant_label || 'Participant';
  const procedure = session.procedure_name || '—';
  // Wall-clock start in the viewer's own timezone, WITH the zone
  // abbreviation (e.g. "3:16 PM CDT") so it's never mistaken for another
  // zone. started_at is a UTC instant; toLocaleTimeString localises it.
  const startedClock = session.started_at
    ? fmtTime(session.started_at)
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: 'error.light',
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{ p: { xs: 1.25, sm: 1.75 }, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <LiveDot />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
              {participant}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {procedure}
              {startedClock ? ` · started ${startedClock}` : ''}
              {timeline ? ` · ${fmt(timeline.durationS)} elapsed` : ''}
            </Typography>
          </Box>
          {onExpand && (
            <Tooltip title="Expand">
              <IconButton
                size="small"
                aria-label={`Expand ${participant}`}
                onClick={() => onExpand(session)}
              >
                <OpenInFullIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* A camera that stopped sending video mid-case (edge agent 0.3.27+).
            Shown on the tile itself — the remote viewer is often the only
            one looking, and the case can still be saved by reseating the
            cable. */}
        {'tenantId' in scope && session.standby && (
          <StandbyGoBanner
            tenantId={(scope as { tenantId: string }).tenantId}
            session={session}
            canGo
          />
        )}
        {(session.frozen_cameras ?? []).map((cam) => (
          <Alert
            key={cam.camera_id}
            severity="error"
            variant="filled"
            sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}
          >
            <strong>{cam.name ?? 'A camera'}</strong> stopped sending video
            {cam.detail ? ` — ${cam.detail}` : ''}
          </Alert>
        ))}

        {/* Body */}
        {!connected && !endedLive ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ flex: 1, py: 3, px: 2, color: 'text.secondary' }}
          >
            <CircularProgress size={20} />
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              {streaming
                ? 'Waiting for sensor data — the device’s sensors are still connecting.'
                : 'Connecting to live stream…'}
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ mt: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {/* Focus — the headline metric on every tile. */}
            <TraitBar
              label="Focus"
              color={getRule('focus').color}
              pct={traitFrame ? traitFrame.focus : null}
              emphasize
            />

            {solo ? (
              <>
                {/* All six traits */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)' },
                    gap: 1.25,
                    mt: 1.5,
                  }}
                >
                  {TRAIT_KEYS.filter((k) => k !== 'focus').map((key) => (
                    <TraitBar
                      key={key}
                      label={getRule(key).label}
                      color={getRule(key).color}
                      pct={traitFrame ? traitFrame[key] : null}
                    />
                  ))}
                </Box>
                {/* EEG bands */}
                {timeline?.hasEeg && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="overline" color="text.secondary">
                      EEG bands (relative power)
                    </Typography>
                    <Box sx={{ height: 120 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={bandWindow} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                          <XAxis type="number" dataKey="t" domain={[Math.max(0, tSec - BAND_WINDOW_S), Math.max(BAND_WINDOW_S, tSec)]} tick={{ fontSize: 9 }} tickFormatter={(v: number) => `${v.toFixed(0)}s`} />
                          <YAxis domain={[0, 1]} tick={{ fontSize: 9 }} width={28} />
                          {BAND_SERIES.map((b) => (
                            <Line key={b.key} type="monotone" dataKey={b.key} stroke={b.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    <BandLegend />
                  </Box>
                )}
              </>
            ) : null}

            {/* Hand-motion trace — on every tile (compact) unless no IMU. */}
            {timeline?.hasImu && (
              <Box sx={{ mt: solo ? 1.5 : 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Hand motion (acceleration, g)
                </Typography>
                <Box sx={{ height: solo ? 100 : 78 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={accelWindow} margin={{ top: 4, right: 8, bottom: 0, left: -28 }}>
                      <CartesianGrid stroke="#eef0f2" strokeDasharray="3 3" />
                      <XAxis type="number" dataKey="t" domain={[Math.max(0, tSec - ACCEL_WINDOW_S), Math.max(ACCEL_WINDOW_S, tSec)]} hide />
                      <YAxis tick={{ fontSize: 9 }} width={24} />
                      <Line type="monotone" dataKey="left" stroke="#4c8dff" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
                      <Line type="monotone" dataKey="right" stroke="#ef5350" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Stack direction="row" spacing={1.25} sx={{ pl: 0.5, flexWrap: 'wrap' }}>
                  <LegendDot color="#4c8dff" label="left hand" />
                  <LegendDot color="#ef5350" label="right hand" />
                </Stack>
              </Box>
            )}

            {endedLive && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Recording ended.
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
